/* ==========================================================================
   Lilac Haven - Carousel widgets

   One custom element, <lh-carousel>, powering every carousel section:
   Recently viewed, You may also like, Customers also bought, New arrivals,
   Shop by product / occasion / recipient.

   Three jobs:

   1. Arrows. The track is a native scroll-snap row (no tiny-slider), so
      "next" is just scrollBy(one page). Arrows disable - not hide - at
      either end so the row never shifts under the cursor, and the whole
      pair is removed when everything already fits.

   2. Arrow position. The reference design centres the arrows on the product
      IMAGE, not on the card (which is taller because of the title, price and
      stars). The image height depends on the column count and the viewport,
      so it is measured from the first card and written back as
      --lhc-arrow-top rather than guessed in CSS.

   3. Async sources. "Recently viewed" and the two recommendation widgets
      have nothing to render server-side on first paint:
        - recently_viewed reads the theme's own bl_recently-viewed-products
          localStorage entry (written by main-product.js) and asks the Section
          Rendering API for this same section filtered to those ids, so the
          cards come back rendered by the same Liquid as everywhere else -
          no duplicate card markup in JavaScript.
        - related / complementary call /recommendations/products with
          section_id, which Shopify renders through this same section.
      Either way the response is this section's own HTML; we lift the track
      out of it and drop it in. If the result is empty the whole section
      hides itself.

   Custom elements upgrade on their own, so this works for sections added or
   re-rendered by the theme editor without any shopify:section:load wiring.
   ========================================================================== */

(function () {
  'use strict';

  if (window.customElements && customElements.get('lh-carousel')) return;

  var ARROW_MIN_DELTA = 2; // px of slack before an end counts as "reached"

  function parseJSON(raw, fallback) {
    if (!raw) return fallback;
    try {
      var out = JSON.parse(raw);
      return out == null ? fallback : out;
    } catch (e) {
      return fallback;
    }
  }

  class LhCarousel extends HTMLElement {
    connectedCallback() {
      if (this.__lhInit) return;
      this.__lhInit = true;

      this.track = this.querySelector('[data-lhc-track]');
      this.prev = this.querySelector('[data-lhc-prev]');
      this.next = this.querySelector('[data-lhc-next]');
      this.source = this.dataset.source || 'collection';

      this.onScroll = this.throttle(this.updateArrows.bind(this));
      this.onResize = this.throttle(this.measure.bind(this));

      if (this.prev) {
        this.prev.addEventListener('click', this.page.bind(this, -1));
      }
      if (this.next) {
        this.next.addEventListener('click', this.page.bind(this, 1));
      }
      window.addEventListener('resize', this.onResize);

      if (this.source === 'recently_viewed') {
        this.loadRecentlyViewed();
      } else if (this.source === 'related' || this.source === 'complementary') {
        this.loadRecommendations();
      } else {
        this.bindTrack();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
    }

    /* --- shared ---------------------------------------------------------- */

    throttle(fn) {
      var frame = null;
      var self = this;
      return function () {
        if (frame) return;
        frame = requestAnimationFrame(function () {
          frame = null;
          fn.call(self);
        });
      };
    }

    bindTrack() {
      this.track = this.querySelector('[data-lhc-track]');
      if (!this.track) return;
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.measure();

      // Images load after first paint and change the media height, so
      // re-measure once each one lands rather than only on resize.
      var imgs = this.track.querySelectorAll('img');
      for (var i = 0; i < imgs.length; i++) {
        if (!imgs[i].complete) {
          imgs[i].addEventListener('load', this.onResize, { once: true });
        }
      }
    }

    measure() {
      if (!this.track) return;
      var media = this.track.querySelector('.lhc-card__media');
      if (media) {
        var h = media.getBoundingClientRect().height;
        if (h > 0) this.style.setProperty('--lhc-arrow-top', Math.round(h / 2) + 'px');
      }
      this.markShortRow();
      this.updateArrows();
    }

    // A row with fewer cards than columns keeps column width and stays
    // left-aligned instead of stretching or floating to the middle; the class
    // does that and also tells the arrows to stand down.
    markShortRow() {
      if (!this.track) return;
      var overflows = this.track.scrollWidth - this.track.clientWidth > ARROW_MIN_DELTA;
      this.track.classList.toggle('lhc__track--short', !overflows);
    }

    updateArrows() {
      if (!this.track || !this.prev || !this.next) return;
      var max = this.track.scrollWidth - this.track.clientWidth;
      if (max <= ARROW_MIN_DELTA) {
        this.prev.hidden = true;
        this.next.hidden = true;
        return;
      }
      this.prev.hidden = false;
      this.next.hidden = false;
      var left = this.track.scrollLeft;
      this.prev.disabled = left <= ARROW_MIN_DELTA;
      this.next.disabled = left >= max - ARROW_MIN_DELTA;
    }

    page(direction) {
      if (!this.track) return;
      // One "page" is the visible width minus a sliver, so the card that was
      // half-visible at the edge is fully on screen after the jump instead of
      // being skipped.
      var step = Math.max(this.track.clientWidth * 0.92, 120);
      // Read the preference at click time, not at load: the OS setting can
      // change while the page is open. The track itself carries no
      // scroll-behavior, so this call is the only thing that animates.
      var still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollBy({
        left: step * direction,
        behavior: still ? 'auto' : 'smooth',
      });
    }

    /* --- async sources --------------------------------------------------- */

    fail() {
      // Nothing to show: hide the whole section rather than leaving a bare
      // heading with an empty row under it.
      if (this.dataset.hideIfEmpty !== 'false') {
        var root = this.closest('[data-lhc-section]') || this;
        root.hidden = true;
      }
      this.dispatchEvent(new CustomEvent('lh-carousel:empty', { bubbles: true }));
    }

    replaceWith(html) {
      var holder = document.createElement('div');
      holder.innerHTML = html;
      var fresh = holder.querySelector('[data-lhc-track]');
      if (!fresh || !fresh.children.length) {
        this.fail();
        return;
      }
      var old = this.querySelector('[data-lhc-track]');
      if (!old) {
        this.fail();
        return;
      }
      old.replaceWith(fresh);
      this.removeAttribute('data-lhc-loading');
      this.bindTrack();
      this.syncWishlist();
    }

    // Re-run the theme's wishlist pass over the cards we just inserted.
    syncWishlist() {
      if (window.LHWishlist && typeof window.LHWishlist.refresh === 'function') {
        window.LHWishlist.refresh();
      } else {
        document.dispatchEvent(new CustomEvent('wishlist:updated'));
      }
    }

    loadRecommendations() {
      var url = this.dataset.url;
      if (!url) {
        this.fail();
        return;
      }
      var self = this;

      // "Customers also bought" asks for complementary products, which only
      // exist once a merchant has curated them in Search & Discovery. An
      // un-curated product returns an empty row, so the section retries once
      // with intent=related rather than disappearing. The fallback URL is only
      // present when the merchant left that setting on.
      var tryUrl = function (target, fallback) {
        fetch(target)
          .then(function (r) {
            if (!r.ok) throw new Error(r.status);
            return r.text();
          })
          .then(function (html) {
            if (fallback && !self.hasCards(html)) {
              tryUrl(fallback, null);
              return;
            }
            self.replaceWith(html);
          })
          .catch(function () {
            if (fallback) {
              tryUrl(fallback, null);
              return;
            }
            self.fail();
          });
      };

      tryUrl(url, this.dataset.fallbackUrl || null);
    }

    hasCards(html) {
      var holder = document.createElement('div');
      holder.innerHTML = html;
      var track = holder.querySelector('[data-lhc-track]');
      return !!(track && track.children.length);
    }

    loadRecentlyViewed() {
      var stored = parseJSON(localStorage.getItem('bl_recently-viewed-products'), []);
      if (!Array.isArray(stored) || !stored.length) {
        this.fail();
        return;
      }

      var limit = parseInt(this.dataset.limit, 10) || 10;
      var excludeId = this.dataset.excludeId || '';
      var ids = [];

      // main-product.js pushes to the end of the array, so the newest entry
      // is last; reverse to show most-recent-first.
      for (var i = stored.length - 1; i >= 0 && ids.length < limit; i--) {
        var entry = stored[i];
        if (!entry || !entry.id) continue;
        var id = String(entry.id);
        if (id === String(excludeId)) continue; // never recommend the page you are on
        if (ids.indexOf(id) === -1) ids.push(id);
      }

      if (!ids.length) {
        this.fail();
        return;
      }

      var query = ids.map(function (id) { return 'id:' + id; }).join(' OR ');
      var url = this.dataset.url + '&q=' + encodeURIComponent(query);

      var self = this;
      fetch(url)
        .then(function (r) {
          if (!r.ok) throw new Error(r.status);
          return r.text();
        })
        .then(function (html) {
          self.replaceWith(self.reorder(html, ids));
        })
        .catch(this.fail.bind(this));
    }

    // Search returns products in relevance order, which for an id: query is
    // arbitrary. Re-sort the returned cards into the order the shopper
    // actually viewed them.
    reorder(html, ids) {
      var holder = document.createElement('div');
      holder.innerHTML = html;
      var track = holder.querySelector('[data-lhc-track]');
      if (!track) return html;
      var items = Array.prototype.slice.call(track.children);
      items.sort(function (a, b) {
        var ai = ids.indexOf(String(a.dataset.productId));
        var bi = ids.indexOf(String(b.dataset.productId));
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      });
      items.forEach(function (li) { track.appendChild(li); });
      return holder.innerHTML;
    }
  }

  customElements.define('lh-carousel', LhCarousel);
})();
